import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMachine, useSelector } from '@xstate/react'
import { checkInMachine, type CheckInStateId, type Insurance } from './machine'
import { CheckInActorProvider } from './context'
import { useFlowBackHandler, useSelfService } from '../../context'
import { useFlowPhaseProgress, type FlowPhaseConfig } from '../use-flow-step-progress'
import { isEebAvailableCached } from '../../../../lib/eeb-availability'
import { FEATURE_FLAGS } from '../../../../platform/defaults'
import { CardCheckStep } from './steps/CardCheckStep'
import { PatientSelectionStep } from './steps/PatientSelectionStep'
import { CardCheckinStep } from './steps/CardCheckinStep'
import { RegisterFromCardStep } from './steps/RegisterFromCardStep'
import { PatientLookupStep } from './steps/PatientLookupStep'
import { CheckingInStep } from './steps/CheckingInStep'
import { AppointmentSelectionStep } from './steps/AppointmentSelectionStep'
import {
  NoAppointmentFoundStep,
  NoAppointmentFoundUnavailableStep,
} from './steps/NoAppointmentFoundStep'
import { EebPromptStep } from './steps/EebPromptStep'
import { PatientRegisterBasicsStep } from './steps/PatientRegisterBasicsStep'
import { PatientRegisterInsurerStep } from './steps/PatientRegisterInsurerStep'
import { PatientRegisterDetailsStep } from './steps/PatientRegisterDetailsStep'
import { DiffReviewStep } from './steps/DiffReviewStep'
import { InsuranceSelectionStep } from './steps/InsuranceSelectionStep'
import { EebRequestStep } from './steps/EebRequestStep'
import { SelfPayStep } from './steps/SelfPayStep'
import { DoneStep } from './steps/DoneStep'
// Quarterly coverage steps
import { QuarterlyCoverageCheckStep } from './steps/QuarterlyCoverageCheckStep'
import { InsurerPreFilledStep } from './steps/InsurerPreFilledStep'
import { InsurerSearchSelectionStep } from './steps/InsurerSearchSelectionStep'
import { InsurerContactStep } from './steps/InsurerContactStep'
import { EebConsentStep } from './steps/EebConsentStep'
import { PatientInfoLoadingStep } from './steps/PatientInfoLoadingStep'
import { PatientInfoReviewStep } from './steps/PatientInfoReviewStep'
import { PatientCreationHandoffStep } from './steps/PatientCreationHandoffStep'
import { ReceptionHelpStep } from './steps/ReceptionHelpStep'
import { reportOperationalError } from '../../../../lib/operational-error-telemetry'
import { docCirrusRpcClient } from '../../../../lib/rpc/doc-cirrus.client'

/** First state of the check-in flow - back from here exits the flow */
const FIRST_STATE: CheckInStateId = 'cardCheck'

/**
 * States where "Zurück" is hidden: a non-cancellable operation is in flight (the check-in commit,
 * the coverage check, the durable eEB acknowledgement) or the check-in is already committed and
 * going back would suggest it could be undone (KC-13, KC-53, KC-63). Each of these screens offers
 * its own forward action instead.
 */
const BACK_HIDDEN_STATES: readonly CheckInStateId[] = [
  'checkingIn',
  'quarterlyCoverageCheck',
  'eebRequest',
  'patientInfoGate',
  'patientInfoLoading',
  'patientInfoReview',
  'patientInfoResult',
  'patientInfoExpired',
  'done',
]

/** Identification states: card, lookup, registration, the commit itself and appointment choice. */
const IDENTIFY_STATES: readonly CheckInStateId[] = [
  'cardCheck',
  'patientSelection',
  'cardCheckin',
  'registerFromCard',
  'patientLookup',
  'diffReview',
  'patientCreationHandoff',
  'receptionHelp',
  'patientRegisterBasics',
  'patientRegisterInsurer',
  'patientRegisterDetails',
  'checkingIn',
  'appointmentSelection',
  'noAppointmentFound',
]

/** The manual registration sub-flow, reported as "Teil x von 3" inside the first phase. */
const REGISTRATION_SUB_STEPS: readonly CheckInStateId[] = [
  'patientRegisterBasics',
  'patientRegisterInsurer',
  'patientRegisterDetails',
]

/** Insurance proof after the committed check-in (coverage check, eEB, self-pay). */
const INSURANCE_STATES: readonly CheckInStateId[] = [
  'insuranceSelection',
  'selfPay',
  'quarterlyCoverageCheck',
  'insurerPreFilled',
  'insurerSearchSelection',
  'insurerContact',
  'eebConsent',
  'eebRequest',
  'eebPrompt',
]

/** Everything after the check-in work is finished: the optional review and the closing screen. */
const COMPLETE_STATES: readonly CheckInStateId[] = [
  'patientInfoGate',
  'patientInfoLoading',
  'patientInfoReview',
  'patientInfoResult',
  'patientInfoExpired',
  'done',
]

interface CheckInFlowProps {
  /**
   * Called when the flow finishes. `nextFlow` redirects into another flow
   * (e.g. 'scheduler' after a no-appointment check-in); `carriedPatient` hands
   * the already-identified patient to that next flow so it can skip its own
   * identification step.
   */
  onComplete?: (
    nextFlow?: 'scheduler' | 'checkin' | 'emergency',
    carriedPatient?: { id: string; insurance?: Insurance }
  ) => void
  schedulerAvailable?: boolean
}

interface CheckInCurrentStepProps {
  currentState: CheckInStateId
  onBookAppointment: () => void
  onComplete: () => void
  schedulerAvailable: boolean
}

function CheckInCurrentStep({
  currentState,
  onBookAppointment,
  onComplete,
  schedulerAvailable,
}: CheckInCurrentStepProps) {
  switch (currentState) {
    case 'cardCheck':
      return <CardCheckStep />

    case 'patientSelection':
      return <PatientSelectionStep />

    case 'cardCheckin':
      return <CardCheckinStep />

    case 'registerFromCard':
      return <RegisterFromCardStep />

    case 'patientLookup':
      return <PatientLookupStep />

    case 'checkingIn':
      return <CheckingInStep />

    case 'appointmentSelection':
      return <AppointmentSelectionStep />

    case 'noAppointmentFound':
      return schedulerAvailable ? (
        <NoAppointmentFoundStep onBookAppointment={onBookAppointment} />
      ) : (
        <NoAppointmentFoundUnavailableStep onComplete={onComplete} />
      )

    case 'eebPrompt':
      return <EebPromptStep />

    case 'patientRegisterBasics':
      return <PatientRegisterBasicsStep />

    case 'patientRegisterInsurer':
      return <PatientRegisterInsurerStep />

    case 'patientRegisterDetails':
      return <PatientRegisterDetailsStep />

    case 'patientCreationHandoff':
      return <PatientCreationHandoffStep onComplete={onComplete} />

    case 'receptionHelp':
      return <ReceptionHelpStep onComplete={onComplete} />

    case 'diffReview':
      return <DiffReviewStep />

    case 'insuranceSelection':
      return <InsuranceSelectionStep />

    case 'eebRequest':
      return <EebRequestStep />

    case 'selfPay':
      return <SelfPayStep />

    case 'quarterlyCoverageCheck':
      return <QuarterlyCoverageCheckStep />

    case 'insurerPreFilled':
      return <InsurerPreFilledStep />

    case 'insurerSearchSelection':
      return <InsurerSearchSelectionStep />

    case 'insurerContact':
      return <InsurerContactStep />

    case 'eebConsent':
      return <EebConsentStep />

    case 'patientInfoGate':
    case 'patientInfoLoading':
      return <PatientInfoLoadingStep />

    // Review and result are one component on purpose: the frozen submitted proposal that makes a
    // retry idempotent lives in its state, and unmounting between the two phases would lose it.
    case 'patientInfoReview':
    case 'patientInfoResult':
    case 'patientInfoExpired':
      return <PatientInfoReviewStep />

    case 'done':
      return <DoneStep onComplete={onComplete} />

    default:
      return <UnknownCheckInState state={currentState} onComplete={onComplete} />
  }
}

/**
 * Recovery for a state this switch does not render (FC-21): report it as an invariant breach and
 * hand the patient to reception instead of printing an English debug line.
 */
function UnknownCheckInState({
  state,
  onComplete,
}: {
  state: string
  onComplete: () => void
}) {
  useEffect(function reportUnknownCheckInState() {
    void reportOperationalError({
      action: 'check-in.state-unrendered',
      severity: 'error',
      source: 'invariant',
      component: 'CheckInFlow',
      error: new Error('Check-in state has no step'),
      context: { state },
    })
  }, [state])
  return <ReceptionHelpStep onComplete={onComplete} />
}

/**
 * Check-in flow orchestrator component.
 * Uses XState to manage the complex multi-step check-in process.
 *
 * Security: The machine instance is created fresh on mount and destroyed on unmount.
 * Patient data is isolated per session - when the flow ends, the machine is garbage collected.
 */
export function CheckInFlow({
  onComplete,
  schedulerAvailable = true,
}: CheckInFlowProps) {
  const [snapshot, send, actorRef] = useMachine(checkInMachine, {
    // The build flag says the feature exists; the server says whether this
    // practice can actually accept a consent right now. Both must hold, and an
    // unknown answer counts as "no" — offering eEB to a practice that cannot
    // accept it walks the patient through consent screens that then fail.
    input: { eebEnabled: FEATURE_FLAGS.eeb && isEebAvailableCached() },
  })
  const { setBackHidden } = useSelfService()

  const currentState = useSelector(actorRef, (s) => s.value as CheckInStateId)
  const carriedPatientId = snapshot.context.patientId
  const carriedInsurance = snapshot.context.insurance
  const { t } = useTranslation()
  const isBackHidden = BACK_HIDDEN_STATES.includes(currentState)
  const phaseConfig = useMemo<FlowPhaseConfig>(
    () => ({
      phases: [
        {
          label: t('self-service.check-in.progress.identify'),
          states: IDENTIFY_STATES,
          subSteps: REGISTRATION_SUB_STEPS,
        },
        { label: t('self-service.check-in.progress.insurance'), states: INSURANCE_STATES },
        { label: t('self-service.check-in.progress.done'), states: [] },
      ],
      completeStates: COMPLETE_STATES,
    }),
    [t]
  )

  useEffect(function ownPendingCardWorkflow() {
    let lastCrlogId = actorRef.getSnapshot().context.crlogId
    const subscription = actorRef.subscribe((state) => {
      const nextId = state.context.crlogId
      if (lastCrlogId && (nextId !== lastCrlogId || state.matches('cardCheck'))) {
        void docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: lastCrlogId })
        lastCrlogId = undefined
      }
      if (!state.matches('cardCheck')) lastCrlogId = nextId
    })
    return function releasePendingCardWorkflow() {
      subscription.unsubscribe()
      if (lastCrlogId) void docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: lastCrlogId })
    }
  }, [actorRef])

  // Named phases with registration as a sub-step; complete once the check-in work is done (KC-02).
  useFlowPhaseProgress(currentState, phaseConfig)

  useEffect(function syncBackHidden() {
    setBackHidden(isBackHidden)
  }, [isBackHidden, setBackHidden])

  // Register back handler with parent - exit flow if at first state, otherwise go back in flow
  const handleBack = useCallback(() => {
    if (currentState === FIRST_STATE) {
      onComplete?.()
    } else {
      send({ type: 'BACK' })
    }
  }, [currentState, onComplete, send])
  const handleComplete = useCallback(() => {
    onComplete?.()
  }, [onComplete])
  const handleBookAppointment = useCallback(() => {
    onComplete?.(
      'scheduler',
      carriedPatientId
        ? { id: carriedPatientId, insurance: carriedInsurance }
        : undefined
    )
  }, [carriedInsurance, carriedPatientId, onComplete])

  useFlowBackHandler(handleBack)

  return (
    <CheckInActorProvider actor={actorRef}>
      <div className="flex flex-col h-full">
        <CheckInCurrentStep
          currentState={currentState}
          onBookAppointment={handleBookAppointment}
          onComplete={handleComplete}
          schedulerAvailable={schedulerAvailable}
        />
      </div>
    </CheckInActorProvider>
  )
}
